import { Controller } from '@hotwired/stimulus'
import Choices from 'choices.js'

export default class extends Controller {
  static targets = ['select', 'options']

  initialize () {
    this.searchPath = this.element.dataset.searchPath
    this.forceOption = this.element.dataset.forceOption || true
  }

  connect () {
    this.#setup()
    document.addEventListener('turbo:morph', this.reload.bind(this))
    this.#toggleInput()
  }

  disconnect () {
    try {
      this.choices.destroy()

      if (this.input && this.searchPath) {
        this.input.removeEventListener('input', this.#search)
      }
      this.selectTarget.removeEventListener('addItem', this.#addItem)
      this.selectTarget.removeEventListener('removeItem', this.#removeItem)
      document.removeEventListener('turbo:morph', this.reload.bind(this))
    } catch {}
  }

  reload () {
    if (this.choices) {
      this.choices.destroy()
      this.#setup()
    }
  }

  enable () {
    this.choices.enable()
  }

  #setup = () => {
    const isFontSelect = this.selectTarget.dataset.fontsSelect === 'true'

    this.choices = new Choices(this.selectTarget, {
      searchPlaceholderValue: 'Search',
      fuseOptions: {
        threshold: 0.2
      },
      allowHTML: true,
      ...this.#options(),
      ...(isFontSelect && {
        callbackOnCreateTemplates: () => ({
          choice: (classNames, data) => {
            const fontName = data.value
            const div = document.createElement('div')
            div.className = [
              classNames.item || 'choices__item',
              classNames.itemChoice || 'choices__item--choice',
              data.disabled
                ? classNames.itemDisabled || 'choices__item--disabled'
                : classNames.itemSelectable || 'choices__item--selectable'
            ]
              .filter(Boolean)
              .join(' ')
            div.setAttribute('data-choice', '')
            div.setAttribute('data-id', data.id)
            div.setAttribute('data-value', data.value)
            if (data.disabled) {
              div.setAttribute('data-choice-disabled', '')
              div.setAttribute('aria-disabled', 'true')
            } else {
              div.setAttribute('data-choice-selectable', '')
            }
            const span = document.createElement('span')
            span.style.fontFamily = `'${fontName}', sans-serif`
            span.textContent = data.label
            div.appendChild(span)
            return div
          }
        })
      })
    })

    this.input = this.element.querySelector('input')
    this.selectTarget.addEventListener('addItem', this.#addItem)
    this.selectTarget.addEventListener('removeItem', this.#removeItem)

    if (this.input && this.searchPath) {
      this.input.addEventListener('input', this.#search)
    }
  }

  #refresh = () => {
    this.choices.setChoices([], 'value', 'label', true)
    if (this.hasOptionsTarget) {
      ;[...this.optionsTarget.children].forEach(this.#appendOption)
    }
  }

  #appendOption = (option) => {
    if (
      ![...this.selectTarget.options].some(o => {
        return o.label === option.label
      })
    ) { this.choices.setChoices([option], 'value', 'label', false) }
  }

  #addItem = (event) => {
    const selectedLength = this.selectTarget.options.length
    const maxItemCount = parseInt(this.element.dataset.maxItemCount)

    if (event.detail.customProperties === 'all') {
      this.choices.choiceList.element.querySelectorAll('.choices__item').forEach(item => {
        this.choices.setChoiceByValue(item.dataset.value)
      })
      this.choices.hideDropdown()
      return this.choices.removeActiveItemsByValue('all')
    }

    if (maxItemCount && selectedLength === maxItemCount) {
      this.choices.hideDropdown()
    }

    if (this.hasOptionsTarget) {
      const option = [...this.optionsTarget.children].find(option => {
        return option.label === event.detail.label
      })

      if (option) {
        option.setAttribute('selected', '')
      } else {
        const newOption = document.createElement('option')
        newOption.setAttribute('label', event.detail.label)
        newOption.setAttribute('value', event.detail.value)
        newOption.setAttribute('selected', '')
        this.optionsTarget.appendChild(newOption)
      }
    }
  }

  #removeItem = (event) => {
    if (this.hasOptionsTarget) {
      const option = [...this.optionsTarget.children].find(item => {
        return item.label === event.detail.label
      })
      if (option) { this.searchPath ? option.remove() : option.removeAttribute('selected') }
    }
    if (this.forceOption && !this.selectTarget.options.length) { this.selectTarget.add(document.createElement('option')) }
  }

  #search = (event) => {
    if (event.target.value) {
      fetch(this.searchPath + `?q=${event.target.value}`, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      })
        .then(response => response.json())
        .then(this.#update)
    } else {
      this.refresh()
    }
  }

  #update = (data) => {
    this.choices.setChoices(data.filter(this.#filter), 'value', 'label', true)
  }

  #filter = (item) => {
    return ![...this.selectTarget.options].some(option => {
      return option.label === item.label
    })
  }

  #options = () => {
    return 'silent renderChoiceLimit maxItemCount addItems removeItems removeItemButton editItems duplicateItemsAllowed delimiter paste searchEnabled searchChoices searchFloor searchResultLimit position resetScrollPosition addItemFilter shouldSort shouldSortItems placeholder placeholderValue prependValue appendValue searchPlaceholderValue renderSelectedChoices loadingText noResultsText noChoicesText itemSelectText addItemText maxItemText'
      .split(' ')
      .reduce(this.#optionsReducer, {})
  }

  #optionsReducer = (accumulator, currentValue) => {
    const value = this.element.dataset[currentValue]
    if (value) {
      accumulator[currentValue] = value === 'true' || value === 'false' ? JSON.parse(value) : value
    } else if (currentValue === 'shouldSort') {
      accumulator[currentValue] = false // Set default value for shouldSort
    }
    return accumulator
  }

  #toggleInput = () => {
    const input = this.element.querySelector('.choices__input.choices__input--cloned')

    if (!input) return

    if (this.selectTarget.length >= parseInt(this.element.dataset.maxItemCount)) {
      input.classList.add('hidden')
    } else {
      input.classList.remove('hidden')
    }
  }
}
